import { CheckSquare, Trash2 } from "lucide-react";

import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";

import {
  countNoun,
  describeBulkResult,
  selectedInView,
  type BulkItem,
  type Noun,
} from "./bulk-delete";
import type { BulkDelete } from "./use-bulk-delete";

/** Opens select mode. A text button, 44px tall on a phone. */
export function BulkSelectButton({
  onClick,
  testId,
}: {
  onClick: () => void;
  testId: string;
}) {
  return (
    <Button
      size="sm"
      variant="outline"
      className="h-11 sm:h-9"
      onClick={onClick}
      data-testid={testId}
    >
      <CheckSquare className="h-4 w-4" />
      Select
    </Button>
  );
}

/**
 * The checkbox on a row in select mode. The label is the 44px target, the box
 * inside it is the size of a normal checkbox, and the row's name is in its
 * accessible name so a screen reader says which row it ticks.
 */
export function BulkRowCheckbox({
  checked,
  onChange,
  name,
  testId,
  withText = false,
  disabled = false,
}: {
  checked: boolean;
  onChange: () => void;
  /** The row's name, for the accessible name. */
  name: string;
  testId: string;
  /** Adds the word "Select" beside the box, for rows with a wide target. */
  withText?: boolean;
  disabled?: boolean;
}) {
  return (
    <label
      className={
        withText
          ? "flex min-h-11 w-full cursor-pointer items-center justify-end gap-3 text-sm text-foreground"
          : "flex min-h-11 min-w-11 shrink-0 cursor-pointer items-center justify-center"
      }
    >
      {withText && <span>{checked ? "Selected" : "Select"}</span>}
      <input
        type="checkbox"
        className="size-5 accent-lit"
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        aria-label={`Select ${name}`}
        data-testid={testId}
      />
    </label>
  );
}

/**
 * The bar above a list in select mode: Select all, how many are selected,
 * Delete selected, and Done. "All" means the rows shown, which a search
 * narrows, so the label says how many that is.
 */
export function BulkSelectBar({
  bulk,
  visible,
  noun,
  busy = false,
  testId,
}: {
  bulk: BulkDelete;
  visible: readonly BulkItem[];
  noun: Noun;
  busy?: boolean;
  testId: string;
}) {
  const chosen = selectedInView(bulk.selected, visible).length;
  const allSelected = visible.length > 0 && chosen === visible.length;
  const running = bulk.phase.name === "running";
  return (
    <div
      className="space-y-2 rounded-lg border border-border bg-muted px-3 py-2"
      data-testid={testId}
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm text-foreground">
          <input
            type="checkbox"
            className="size-5 accent-lit"
            checked={allSelected}
            disabled={visible.length === 0 || running}
            onChange={() => bulk.toggleAll(visible)}
            data-testid={`${testId}-all`}
          />
          Select all {countNoun(visible.length, noun)}
        </label>
        <p
          className="text-sm text-muted-foreground"
          role="status"
          data-testid={`${testId}-count`}
        >
          {chosen} selected
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="destructive"
          className="h-11 max-sm:flex-1 sm:h-9"
          disabled={chosen === 0 || busy || running}
          onClick={() => bulk.ask(visible)}
          data-testid={`${testId}-delete`}
        >
          <Trash2 className="h-4 w-4" />
          Delete selected
          {chosen > 0 ? ` (${chosen})` : ""}
        </Button>
        <Button
          variant="outline"
          className="h-11 max-sm:flex-1 sm:h-9"
          disabled={running}
          onClick={bulk.exit}
          data-testid={`${testId}-done`}
        >
          Done
        </Button>
      </div>
    </div>
  );
}

/**
 * The confirmation, the progress and the outcome of a multi-select delete, in
 * one dialog that cannot be dismissed while it is deleting.
 *
 * It names what will go (the same words the single delete says, passed as
 * `consequences`), then counts "Deleting 2 of 5" while the rows go one at a
 * time, then reports how many were deleted and lists each failure with its
 * reason. Failed rows stay selected behind it.
 */
export function BulkDeleteDialog({
  bulk,
  noun,
  consequences,
  testId,
}: {
  bulk: BulkDelete;
  noun: Noun;
  /** What else the delete does, as the single delete's dialog says it. */
  consequences?: string;
  testId: string;
}) {
  const { phase } = bulk;
  if (phase.name === "idle") return null;
  const running = phase.name === "running";

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !running) bulk.cancel();
      }}
    >
      <DialogContent data-testid={testId} hideClose={running}>
        {phase.name === "done" ? (
          <>
            <DialogHeader>
              <DialogTitle>
                {phase.result.failed.length === 0
                  ? "Deleted"
                  : "Some could not be deleted"}
              </DialogTitle>
            </DialogHeader>
            <p
              role="status"
              className="text-sm text-foreground"
              data-testid={`${testId}-result`}
            >
              {describeBulkResult(phase.result, noun)}
            </p>
            {phase.result.failed.length > 0 && (
              <ul
                className="max-h-60 space-y-2 overflow-y-auto text-sm"
                data-testid={`${testId}-failures`}
              >
                {phase.result.failed.map((item) => (
                  <li key={item.id} className="min-w-0">
                    <p className="break-words font-medium text-foreground">
                      {item.label}
                    </p>
                    <p className="break-words text-destructive-text">
                      {item.error}
                    </p>
                  </li>
                ))}
              </ul>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                className="h-11 sm:h-9"
                onClick={bulk.cancel}
                data-testid={`${testId}-close`}
              >
                Close
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>
                Delete {countNoun(phase.items.length, noun)}?
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm text-muted-foreground">
              {/* Every name, in a box that scrolls: a row the organiser cannot see
                  on the page, such as one in a collapsed day, is still named
                  here before it goes. */}
              <ul
                className="max-h-48 space-y-0.5 overflow-y-auto text-foreground"
                data-testid={`${testId}-names`}
              >
                {phase.items.map((item) => (
                  <li key={item.id} className="break-words">
                    {item.label}
                  </li>
                ))}
              </ul>
              <p>
                {phase.items.length === 1 ? "It is" : "They are"} removed
                {phase.items.length === 1 ? "" : ", one at a time"}.
                {consequences ? ` ${consequences}` : ""} This cannot be undone.
              </p>
            </div>
            {running && (
              <p
                role="status"
                className="text-sm text-foreground"
                data-testid={`${testId}-progress`}
              >
                Deleting {Math.min(phase.done + 1, phase.items.length)} of{" "}
                {phase.items.length}…
              </p>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                className="h-11 sm:h-9"
                disabled={running}
                onClick={bulk.cancel}
                data-testid={`${testId}-cancel`}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="h-11 sm:h-9"
                disabled={running}
                onClick={() => void bulk.confirm()}
                data-testid={`${testId}-confirm`}
              >
                <Trash2 className="h-4 w-4" />
                Delete {phase.items.length}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
